import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";

function IslamicGuide62() {
  const contentRef = useRef(null);
  const titleText = "The Qīmah of Bitcoin: An Inquiry into Its Worth";
  const links = {
    prev: "/islamicguide-62",
    index: "/indexislamic-guide",
    next: "/islamicguide-63",
  };

  const questions = [
    {
      text: "1. What is the main misunderstanding behind the critique that Bitcoin has no intrinsic value?",
      options: [
        {
          value: "A",
          label: "A. It assumes intrinsic value is an inherent property",
        },
        {
          value: "B",
          label: "B. It ignores Bitcoin’s connection to gold reserves",
        },
        {
          value: "C",
          label: "C. It assumes intrinsic value must be physical and tangible",
        },
        {
          value: "D",
          label:
            "D. It overlooks government recognition as the source of value",
        },
      ],
      correct: "A",
    },
    {
      text: "2. According to the lesson, what truly determines whether something has intrinsic value?",
      options: [
        { value: "A", label: "A. The government declaring it legal tender" },
        { value: "B", label: "B. The inherent qualities of the object itself" },
        { value: "C", label: "C. Its backing by physical commodities" },
        {
          value: "D",
          label: "D. The consensus and judgment of free market participants",
        },
      ],
      correct: "D",
    },
    {
      text: "3. In Islamic history, why did commodities like cattle or grains sometimes attain the role of money?",
      options: [
        { value: "A", label: "A. Because rulers forced people to use them" },
        {
          value: "B",
          label: "B. Because they had hidden precious metals inside",
        },
        {
          value: "C",
          label:
            "C. Because they were useful for barter and accepted by participants in the market",
        },
        { value: "D", label: "D. Because they were indestructible" },
      ],
      correct: "C",
    },
    {
      text: "4. Which of the following attributes gives Bitcoin its market value despite lacking physical form?",
      options: [
        { value: "A", label: "A. It can be endlessly printed like fiat money" },
        { value: "B", label: "B. It is backed by land and property" },
        {
          value: "C",
          label:
            "C. Its scarcity, security, and acceptance by a global free market",
        },
        { value: "D", label: "D. It relies only on speculation for its price" },
      ],
      correct: "C",
    },
  ];

  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);

  return (
    <div className="container">
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={62}
        timerMinutes={60}
        currentLevel={5}
        unlockedBadges={9}
      />

      <p className="content" ref={contentRef}>
        Welcome to <b>Day 62</b>! In this lesson we will explore one of the most
        persistent critiques of Bitcoin: the claim that it has no intrinsic
        value. Many dismiss it as worthless because it is neither gold nor
        silver, nor any physical commodity. Yet this misunderstanding comes from
        a flawed view of what value truly is. Value is not embedded inside an
        object but arises from human judgment, shared consensus, and usefulness
        in fulfilling specific needs. Throughout history, societies have chosen
        many different things to serve as money, from livestock to grains, not
        because these items carried a built-in monetary essence but because
        communities agreed to accept them in trade. Today, Bitcoin’s attributes
        such as its scarcity, transparency, energy-backed creation, and
        portability across space and time are what make it valuable to millions
        around the world. This lesson will examine why intrinsic value is
        subjective, how Bitcoin fulfills the qualities of sound money, and why
        it should not be dismissed as lacking worth.
        <br />
        <br />
        <b>Unraveling the Notion of Intrinsic Value</b>
        <br />
        <br />
        One of the most common critiques leveled against Bitcoin is that it has
        “no intrinsic value.” Critics often compare it unfavorably with gold,
        silver, or other tangible commodities, arguing that unlike these metals,
        Bitcoin cannot be touched, melted down, or used in jewelry, industry, or
        other “real-world” applications. This objection, though widely repeated,
        rests upon a misunderstanding of what intrinsic value actually means. To
        properly address this concern, we must peel back the layers of what
        people assume value to be, and how human beings have historically
        arrived at consensus regarding what holds worth.
        <br />
        Intrinsic value is often imagined as something inherent within an
        object, something permanent, undeniable, and independent of human
        perception. A piece of gold is thought to carry worth by itself, whether
        anyone recognizes it or not. Yet, a careful examination of history and
        economics shows that value has always been tied to human judgment.
        Nothing has automatic worth unless people ascribe it. Wheat feeds a
        hungry person, but to someone who cannot eat wheat, it has little
        utility. Diamonds may fetch high prices in markets, but in times of
        famine, people have traded precious stones for bread. This illustrates
        that value is not absolute. It is always relational, determined by
        circumstances and human needs.
        <br />
        Intrinsic value is often imagined as something inherent within an
        object, something permanent, undeniable, and independent of human
        perception. A piece of gold is thought to carry worth by itself, whether
        anyone recognizes it or not. Yet, a careful examination of history and
        economics shows that value has always been tied to human judgment.
        Nothing has automatic worth unless people ascribe it. Wheat feeds a
        hungry person, but to someone who cannot eat wheat, it has little
        utility. Diamonds may fetch high prices in markets, but in times of
        famine, people have traded precious stones for bread. This illustrates
        that value is not absolute. It is always relational, determined by
        circumstances and human needs.
        <br />
        When critics say Bitcoin has no intrinsic value, what they mean is that
        it does not serve the same industrial or ornamental purposes as metals.
        But this critique fails to recognize that money itself has never been
        about industrial use alone. The essence of money lies in its ability to
        serve as a medium of exchange, a unit of account, and a store of value.
        An object’s worth as money emerges from its attributes and its
        acceptance by the community that uses it. Value, therefore, is not an
        unchanging property locked inside matter, but a subjective reality
        created and sustained by human action and consensus.
        <br />
        <br />
        <b>Lessons from Islamic History: When Commodities Became Money</b>
        <br />
        <br />
        If we turn to Islamic history, the reality of subjective value becomes
        even clearer. The Qur’an and Sunnah document periods when cattle,
        grains, and other goods were used in barter. These items were not
        created for the purpose of functioning as money; they were originally
        meant for nourishment, clothing, or survival. Yet, communities chose to
        use them as mediums of exchange. The intrinsic worth of these items did
        not suddenly transform. What changed was the perception of participants
        in the marketplace who found them convenient for trade.
        <br />
        If we turn to Islamic history, the reality of subjective value becomes
        even clearer. The Qur’an and Sunnah document periods when cattle,
        grains, and other goods were used in barter. These items were not
        created for the purpose of functioning as money; they were originally
        meant for nourishment, clothing, or survival. Yet, communities chose to
        use them as mediums of exchange. The intrinsic worth of these items did
        not suddenly transform. What changed was the perception of participants
        in the marketplace who found them convenient for trade.
        <br />
        Take the example of livestock. A camel, sheep, or cow has clear
        practical functions: it provides meat, milk, leather, and
        transportation. But in markets of the past, animals were also used as
        units of value. A man could settle a debt with a number of goats, or pay
        a bride price with camels. The “intrinsic” usefulness of animals as food
        or beasts of burden was not what made them money. Instead, it was the
        shared understanding in society that they could be reliably exchanged,
        stored, and transferred.
        <br />
        Similarly, food items such as dates and grains were used in trade,
        especially in early marketplaces of Arabia. A merchant could pay his
        laborers with sacks of barley or use wheat to settle obligations. In
        this sense, food became money not because they were created with “money
        essence,” but because people saw it as practical, divisible, portable,
        and useful for settlement. Its role as money was an outcome of human
        choice, not divine decree of intrinsic monetary power.
        <br />
        <br />
        <b>Bitcoin’s Unique Attributes: Why the Market Grants it Value</b>
        <br />
        <br />
        Bitcoin’s Unique Attributes: Why the Market Grants it Value
        <br />
        Having understood that intrinsic value is subjective and determined by
        markets, we must now ask: why does Bitcoin, in particular, attract
        value? Why do millions of participants across the globe willingly
        exchange real goods and services for it? The answer lies in the
        attributes of Bitcoin that make it uniquely suited for the role of money
        in the digital age.
        <br />
        First and foremost, Bitcoin operates on an immutable ledger. Every
        transaction ever made is recorded transparently and cannot be altered.
        This gives it a level of security and trustworthiness unmatched by any
        fiat system where records can be manipulated or hidden. In a world where
        fraud, corruption, and inflationary tampering erode confidence in
        currencies, Bitcoin provides a pure, incorruptible record of economic
        activity.
        <br />
        Second, Bitcoin is created through proof of work, which requires
        real-world energy and computational power. Unlike fiat currencies that
        can be created at will by central planners with a keystroke, Bitcoin’s
        supply is anchored in work, time, and resources. This ties it to the
        physical reality of human effort, ensuring scarcity and preventing
        manipulation.
        <br />
        Third, Bitcoin is auditable by anyone. Every node in the network carries
        a copy of the ledger, and every participant can verify the supply at any
        moment. No hidden printing, no secret issuance, no surprise devaluations
        are possible. This transparency gives Bitcoin a quality of honesty that
        fiat money lacks.
        <br />
        Finally, Bitcoin is uniquely saleable across both space and time. In
        earlier eras, gold was prized because it could preserve value over
        generations and travel across continents. Bitcoin takes this to the next
        level. It can be sent instantly anywhere in the world, stored safely for
        decades, and divided into tiny units without loss. It combines the
        durability of gold with the speed of digital communication, giving it a
        superior monetary profile.
        <br />
        <br />
        <b>Bitcoin and the Muslim Perspective on Qima</b>
        <br />
        <br />
        The central claim that Bitcoin lacks intrinsic value collapses once we
        recognize that intrinsic value is not objective, permanent, or inherent.
        Value is a judgment formed by people, not a chemical property of matter.
        Just as Muslims in the past used cattle, salt, or food as money when
        they found it suitable, so too do people today assign value to Bitcoin
        because of its attributes.
        <br />
        The central claim that Bitcoin lacks intrinsic value collapses once we
        recognize that intrinsic value is not objective, permanent, or inherent.
        Value is a judgment formed by people, not a chemical property of matter.
        Just as Muslims in the past used cattle, salt, or food as money when
        they found it suitable, so too do people today assign value to Bitcoin
        because of its attributes.
        <br />
        In the Islamic tradition, wealth (maal) is not tied exclusively to
        metals or physical objects. Anything that fulfills human needs, can be
        possessed, and is accepted in exchange can carry value. By this
        definition, Bitcoin comfortably fits within the framework of what
        constitutes wealth. It is scarce, transferable, and widely accepted. Its
        worth is validated not by decree but by voluntary adoption in the
        marketplace.
        <br />
        To dismiss Bitcoin as valueless because it is digital is to
        misunderstand both history and the dynamics of human choice. Value is
        not fixed by appearance but by use. Food became money when people needed
        it for trade. Gold became money when its durability and divisibility
        made it practical. Bitcoin has become money because it solves the
        problems of fiat currency, providing scarcity, honesty, and borderless
        transfer in a digital era.
        <br />
        The critique that Bitcoin has no intrinsic value is a mirage. Intrinsic
        value itself is not objective, but subjective, shaped by human
        perception and free-market consensus. What matters is not whether
        Bitcoin can be worn or eaten but whether it functions as reliable money.
        By all measures, it does. And in a world suffocated by fiat systems,
        Muslims should recognize that Bitcoin offers not only financial
        independence but also alignment with principles of honesty, fairness,
        and transparency.
        <br />
        <br />
        <b>Summary of Today’s Lesson</b>
        <br />
        <br />
        The lesson refutes the common critique that Bitcoin has no intrinsic
        value by showing that intrinsic value is not objective but subjective,
        shaped by human judgment and market consensus. Throughout history,
        people have used cattle, grains, salt, and other commodities as money
        not because they carried inherent “money essence,” but because
        communities collectively agreed to treat them as valuable for trade.
        Likewise, Bitcoin gains worth from its unique attributes: an immutable
        and transparent ledger, proof of work backed by real energy, finite
        supply, universal auditability, and superior portability across space
        and time. These qualities make it far more reliable than fiat money,
        which can be endlessly inflated by central planners. For Muslims,
        Bitcoin aligns with the principle of qima i.e anything scarce,
        transferable, and accepted in exchange carries value. Just as past
        societies adopted practical goods as money, today people have chosen
        Bitcoin for its honesty, fairness, and incorruptibility. The claim that
        Bitcoin lacks intrinsic value dissolves once we understand that value is
        subjective and that Bitcoin’s worth lies in its role as sound money in
        the modern age.
        <br />
        <br />
        <b> Coming Up Next</b>
        <br />
        <br />
        As we move from understanding the inherent worth of Bitcoin, in the next
        lesson we will address another common critique: the claim that Bitcoin
        cannot be money because it is not backed by a state or issued by a
        central authority, and that its pseudonymous inventor weakens its
        legitimacy. Day 63 will explore why decentralisation is a strength
        rather than a weakness, how money in Islamic history emerged from free
        market forces without central planners, and why the unknown identity of
        Bitcoin’s creator does not diminish its validity. Instead, we will see
        that Bitcoin’s open, verifiable, and decentralised structure aligns with
        both timeless economic principles and Islamic values.
      </p>
      <h2>Test your understanding</h2>

      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)}
        />
      ))}

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>

      <FloatingPageNav
        prevLink={links.prev}
        indexLink={links.index}
        nextLink={links.next}
      />
    </div>
  );
}

export default IslamicGuide62;
